import { useState, useEffect, useCallback } from 'react';
import axios from '../axiosConfig';
import { toast } from 'react-toastify';
import { ZodError, ZodOptional } from 'zod';
import { useTimeParser } from './useTimeParser';

export default function useCrud(
    endpoints,
    {
        initForm = () => ({}),
        keyField,
        transformRequest,
        columns = [],
        schema = null
    } = {}
) {
    const { parseTime } = useTimeParser();

    // — Estados de listado y acción —
    const [data, setData] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [loadingAction, setLoadingAction] = useState(false);

    // — Formulario y validaciones —
    const [formData, setFormData] = useState(initForm());
    const [errors, setErrors] = useState({});

    // — Control de modal y tipo de acción —
    const [showModal, setShowModal] = useState(false);
    const [actionType, setActionType] = useState(''); // 'crear' | 'editar' | 'consultar'

    // — Estados adicionales para manejo de modal —
    const [loadingModal] = useState(false);

    // — Helper para extraer arrays de distintas respuestas —
    const extractArray = payload => {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload.data)) return payload.data;
        if (Array.isArray(payload.preguntas)) return payload.preguntas;
        if (Array.isArray(payload.respuestas)) return payload.respuestas;
        for (const key of Object.keys(payload)) {
            const val = payload[key];
            if (val && Array.isArray(val.data)) return val.data;
            if (Array.isArray(val)) return val;
        }
        return [];
    };

    // — Helper para manejar errores del servidor —
    const handleServerError = (err, defaultMessage = 'Error del servidor') => {
        const errorData = err.response?.data;
        
        if (errorData) {
            // Prioridad 1: Mostrar mensaje principal del servidor
            if (errorData.message) {
                toast.error(errorData.message);
                return errorData.message;
            }
            
            // Prioridad 2: Mostrar primer error específico
            if (errorData.errors) {
                const firstError = Object.values(errorData.errors)[0];
                const errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
                toast.error(errorMessage || defaultMessage);
                return errorMessage || defaultMessage;
            }
            
            // Prioridad 3: Mensaje por defecto
            toast.error(defaultMessage);
            return defaultMessage;
        } else {
            // Error de red o sin respuesta del servidor
            const message = err.message || 'Error de conexión';
            toast.error(message);
            return message;
        }
    };

    // — Validación con Zod (cliente) —
    const validateWithZod = () => {
        try {
            // Validar directamente con los datos del formulario
            schema.parse(formData);
            return null;
        } catch (err) {
            if (!(err instanceof ZodError)) throw err;
            const shape = schema._def.shape();
            const fields = err.formErrors.fieldErrors;
            const entries = Object.entries(fields)
                .filter(([field]) => {
                    const isOpt = shape[field] instanceof ZodOptional;
                    const val = formData[field];
                    return !(isOpt && (val === undefined || String(val).trim() === ''));
                })
                .map(([field, msgs]) => {
                    const arr = Array.isArray(msgs) ? msgs : [msgs];
                    const val = formData[field];
                    let msg;
                    if (val === undefined || String(val).trim() === '') {
                        msg = arr.find(m => /requerid/i.test(m)) || 'Requerido';
                    } else {
                        const others = arr.filter(m => !/requerid/i.test(m));
                        msg = others[0] || arr[0];
                    }
                    return [field, msg];
                });
            return Object.fromEntries(entries);
        }
    };

    // — Mapeo de errores del servidor —
    const mapServerErrors = rawErrors => {
        return Object.fromEntries(
            Object.entries(rawErrors).map(([field, msgs]) => {
                const col = columns.find(c => c.key === field);
                const label = col?.label || field;
                
                // Asegurar que text sea siempre una cadena
                let text;
                if (Array.isArray(msgs)) {
                    text = msgs.join(' ');
                } else if (typeof msgs === 'string') {
                    text = msgs;
                } else if (typeof msgs === 'object' && msgs !== null) {
                    // Si es un objeto, intentar extraer el mensaje
                    text = msgs.message || msgs.error || JSON.stringify(msgs);
                } else {
                    text = String(msgs || '');
                }
                
                // Validar que text sea string antes de usar métodos de string
                if (typeof text !== 'string') {
                    text = String(text);
                }
                
                // Reemplazar el nombre del campo con la etiqueta si aparece en el mensaje
                if (new RegExp(field, 'g').test(text)) {
                    return [field, text.replace(new RegExp(field, 'g'), label)];
                }
                
                // Reemplazar el nombre humanizado del campo
                const human = field.replace(/_/g, ' ');
                if (new RegExp(human, 'gi').test(text)) {
                    return [field, text.replace(new RegExp(human, 'gi'), label)];
                }
                
                // Intentar extraer la parte final del mensaje de error
                const dyn = text.match(/(es\s.*)$/i);
                const tail = dyn ? ` ${dyn[1]}` : '';
                
                return [field, `Campo ${label}${tail}`];
            })
        );
    };

    // — Listar registros —
    const list = useCallback(async (params = {}) => {
        setLoadingList(true);
        try {
            if (!endpoints.listar) throw new Error('Endpoint "listar" no definido');
            const query = new URLSearchParams(params).toString();
            const url = endpoints.listar + (query ? `?${query}` : '');
            const res = await axios.get(url);
            setData(extractArray(res.data));
        } catch (err) {
            handleServerError(err, 'Error al cargar datos');
        } finally {
            setLoadingList(false);
        }
    }, [endpoints]);

    useEffect(() => {
        list();
    }, [list]);

    // — Unifica POST/PUT según acción —
    const actionRequest = useCallback(async () => {
        // Enviar datos del formulario directamente (el servidor acepta HH:MM)
        const body = transformRequest ? transformRequest(formData) : formData;
        
        if (actionType === 'crear') {
            if (!endpoints.crear) throw new Error('Endpoint "crear" no definido');
            return axios.post(endpoints.crear, body);
        } else {
            if (!endpoints.actualizar) throw new Error('Endpoint "actualizar" no definido');
            const id = formData[keyField];
            
            if (!id) {
                throw new Error(`ID no encontrado para actualizar. KeyField: ${keyField}`);
            }
            
            return axios.put(endpoints.actualizar(id), body);
        }
    }, [actionType, formData, endpoints, keyField, transformRequest]);

    // — Crear/Editar centralizado en submit() —
    async function submit() {
        if (schema) {
            const zodErrs = validateWithZod();
            if (zodErrs) {
                setErrors(zodErrs);
                return;
            }
        }

        setLoadingAction(true);
        try {
            const res = await actionRequest();
            await list();
            toast.success(res.data.message || 'Operación exitosa');
            setShowModal(false);
        } catch (err) {
            const errorData = err.response?.data;
            
            // Manejar errores del servidor
            if (errorData) {
                // Mostrar mensaje principal del servidor
                if (errorData.message) {
                    toast.error(errorData.message);
                }
                
                // Manejar errores de validación
                if (errorData.errors) {
                    // Mapear errores para mostrar en el formulario
                    setErrors(mapServerErrors(errorData.errors));
                    
                    // Mostrar CADA error específico como toast con delay
                    const allErrors = Object.values(errorData.errors).flat();
                    
                    // Limitar a máximo 5 errores para evitar spam
                    const maxErrors = Math.min(allErrors.length, 5);
                    
                    allErrors.slice(0, maxErrors).forEach((error, index) => {
                        // Agregar delay progresivo para que no aparezcan todos a la vez
                        setTimeout(() => {
                            toast.error(error);
                        }, index * 100); // 100ms de delay entre cada toast
                    });
                    
                    // Si hay más de 5 errores, mostrar un toast adicional
                    if (allErrors.length > 5) {
                        setTimeout(() => {
                            toast.error(`Y ${allErrors.length - 5} errores más...`);
                        }, 500);
                    }
                }
                
                // Si no hay message ni errors, mostrar error genérico
                if (!errorData.message && !errorData.errors) {
                    toast.error('Error interno del servidor');
                }
            } else {
                // Error de red o sin respuesta del servidor
                toast.error(err.message || 'Error de conexión');
            }
        } finally {
            setLoadingAction(false);
        }
    }

    // — Eliminar sin modal —
    const remove = useCallback(async id => {
        setLoadingAction(true);
        try {
            if (!endpoints.borrar) throw new Error('Endpoint "borrar" no definido');
            const url = typeof endpoints.borrar === 'function'
                ? endpoints.borrar(id)
                : endpoints.borrar;
            const res = await axios.delete(url);
            
            const responseData = res.data;
            
            // Verificar si la operación fue exitosa
            if (responseData.success !== false) {
                await list();
                toast.success(responseData.message || 'Eliminado correctamente');
            } else {
                // Si success es false, mostrar el mensaje de error del servidor
                toast.error(responseData.message || 'Error al eliminar');
            }
            
        } catch (err) {
            handleServerError(err, 'Error al eliminar');
        } finally {
            setLoadingAction(false);
        }
    }, [endpoints, list]);

    // — Consultar un elemento específico —
    const consultar = useCallback(async (id) => {
        if (!endpoints.consultar) {
            return null;
        }
        
        try {
            const res = await axios.get(endpoints.consultar(id));
            
            // Para consultar un elemento individual, no usar extractArray
            // ya que necesitamos el objeto completo, no un array
            let datos = res.data;
            
            // Si viene envuelto en un objeto con propiedades como { success, data, empresa, etc }
            if (datos && typeof datos === 'object' && !Array.isArray(datos)) {
                // Buscar la propiedad que contenga los datos
                if (datos.host) {
                    datos = datos.host;
                } else if (datos.empresa) {
                    datos = datos.empresa;
                } else if (datos.pregunta) {
                    datos = datos.pregunta;
                } else if (datos.manifiesto) { 
                    datos = datos.manifiesto;
                } else if (datos.data && !Array.isArray(datos.data)) {
                    datos = datos.data;
                }
            }

            return datos;
        } catch (err) {
            handleServerError(err, 'Error al consultar datos');
            return null;
        }
    }, [endpoints]);

    // — Manejadores de formulario y modal —
    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        
        let processedValue;
        if (type === 'checkbox') {
            processedValue = checked;
        } else if (value === 'true') {
            processedValue = true;
        } else if (value === 'false') {
            processedValue = false;
        } else {
            processedValue = value;
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));
        
        // Limpiar error específico del campo cuando el usuario empieza a escribir
        setErrors(prev => ({ ...prev, [name]: null }));
    }, []);

    const openModal = useCallback(async (type, item = null) => {
        setActionType(type);
        setErrors({});
        
        if (type === 'crear') {
            setFormData(initForm());
            setShowModal(true);
        } else if (type === 'editar' && item) {
            setShowModal(true);
            
            // Si existe endpoint consultar, usarlo para obtener datos completos
            if (endpoints.consultar) {
                const datosCompletos = await consultar(item[keyField]);
                if (datosCompletos) {
                    // Parsear campos de tiempo para el formulario
                    const parsedData = { ...datosCompletos };
                    columns.forEach(col => {
                        if (col.type === 'time' && parsedData[col.key]) {
                            parsedData[col.key] = parseTime(parsedData[col.key]);
                        }
                    });
                    setFormData(parsedData);
                } else {
                    // Fallback: usar datos de la tabla
                    const parsedData = { ...item };
                    columns.forEach(col => {
                        if (col.type === 'time' && parsedData[col.key]) {
                            parsedData[col.key] = parseTime(parsedData[col.key]);
                        }
                    });
                    setFormData(parsedData);
                }
            } else {
                // Usar directamente los datos de la tabla
                const parsedData = { ...item };
                columns.forEach(col => {
                    if (col.type === 'time' && parsedData[col.key]) {
                        parsedData[col.key] = parseTime(parsedData[col.key]);
                    }
                });
                setFormData(parsedData);
            }
        } else if (type === 'consultar' && item) {
            setShowModal(true);
            
            if (endpoints.consultar) {
                // Mostrar datos básicos inmediatamente
                setFormData(item ? { ...item } : initForm());
                
                // Luego obtener datos completos del servidor
                const datosCompletos = await consultar(item[keyField]);
                if (datosCompletos) {
                    // Parsear campos de tiempo si es necesario
                    const parsedData = { ...datosCompletos };
                    columns.forEach(col => {
                        if (col.type === 'time' && parsedData[col.key]) {
                            parsedData[col.key] = parseTime(parsedData[col.key]);
                        }
                    });
                    setFormData(parsedData);
                }
            } else {
                // Fallback: usar los datos de la tabla directamente
                setFormData(item ? { ...item } : initForm());
            }
        } else {
            // Para otros casos
            setFormData(item ? { ...item } : initForm());
            setShowModal(true);
        }
    }, [initForm, endpoints.consultar, consultar, keyField, columns, parseTime]);

    const closeModal = useCallback(() => {
        setShowModal(false);
        setErrors({});
    }, []);

    return {
        // datos y estados
        data,
        loading: loadingList,
        loadingAction,
        loadingModal,

        // formulario y modal
        formData,
        setFormData,
        errors,
        showModal,
        actionType,

        // acciones
        list,
        submit,
        remove,
        consultar,

        // utilitarios
        openModal,
        closeModal,
        handleChange
    };
}
